import crypto from "crypto";

import type { ErrorContext } from "@/errors";
import { ExceptionType } from "@/errors";
import { handleEncryptionError } from "@/errors/services";

export class EncryptionService {
  private static ALGORITHM = "aes-256-cbc";
  private static IV_LENGTH = 16; // For AES, this is always 16
  private static SALT_BYTES = 16;
  private static ENCRYPTION_KEY_LENGTH = 32;
  private ENCRYPTION_SECRET: string;

  constructor(private readonly encryptionSecret: string) {
    this.ENCRYPTION_SECRET = encryptionSecret;
  }

  public encryptString(value: string): string {
    try {
      const salt = crypto
        .randomBytes(EncryptionService.SALT_BYTES)
        .toString("hex");
      const iv = crypto.randomBytes(EncryptionService.IV_LENGTH);
      const encryptionKey = crypto.scryptSync(
        this.ENCRYPTION_SECRET,
        salt,
        EncryptionService.ENCRYPTION_KEY_LENGTH,
      );
      const cipher = crypto.createCipheriv(
        EncryptionService.ALGORITHM,
        encryptionKey,
        iv,
      );
      let encrypted = cipher.update(value, "utf8", "hex");
      encrypted += cipher.final("hex");
      return `${iv.toString("hex")}:${encrypted}:${salt}`;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "encryptString",
        service: ExceptionType.Encryption,
        metadata: {
          message: "Failed to encrypt string",
          errorType: error instanceof Error ? error.name : typeof error,
        },
      };
      return handleEncryptionError(error, errorContext, "encryption_failed");
    }
  }

  public decryptString(value: string): string {
    try {
      const [ivHex, encryptedText, salt] = value.split(":");

      if (!ivHex || !encryptedText || !salt) {
        throw new Error("Invalid encrypted string");
      }

      const iv = Buffer.from(ivHex, "hex");
      const encryptionKey = crypto.scryptSync(
        this.ENCRYPTION_SECRET,
        salt,
        EncryptionService.ENCRYPTION_KEY_LENGTH,
      );

      const decipher = crypto.createDecipheriv(
        EncryptionService.ALGORITHM,
        encryptionKey,
        iv,
      );

      let decrypted = decipher.update(encryptedText, "hex", "utf8");
      decrypted += decipher.final("utf8");

      return decrypted;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "decryptString",
        service: ExceptionType.Encryption,
        metadata: {
          message: "Failed to decrypt string",
          errorType: error instanceof Error ? error.name : typeof error,
        },
      };
      return handleEncryptionError(error, errorContext, "decryption_failed");
    }
  }
}
